import { AxiosError } from 'axios'
import { PencilLine } from 'lucide-react'
import { toast } from 'sonner'

import type { UpdateReceiptItemParams, UpdateReceiptParams } from '@/api/types/receipt'
import { Button } from '@/components/ui/button'
import { confirmDialog } from '@/lib/confirm-dialog'

import { formatAmount } from '../field-value'
import {
  useConfirmReceipt,
  useDeleteReceipt,
  useReceipt,
  useUpdateReceipt,
  useUpdateReceiptItem,
} from '../hooks/use-receipts'
import { ReceiptFieldGrid } from './receipt-field-grid'
import { ReceiptIssuePanel } from './receipt-issue-panel'
import { ReceiptItemTable } from './receipt-item-table'
import { ReceiptPdfView } from './receipt-pdf-view'
import { ReceiptPlaceSection } from './receipt-place-section'

const notifyError = (error: unknown, fallback: string) => {
  if (error instanceof AxiosError) {
    toast.error(error.message)
    return
  }
  toast.error(fallback)
}

type Props = {
  receiptId: number
  /** このレシートを含む、同じタブに残っている件数 */
  remainingCount: number
  onClose: () => void
  /** 確定した後に次のレシートへ送る。キューが空なら呼び出し側で一覧に戻す */
  onConfirmed: () => void
}

export const ReceiptDetail = ({ receiptId, remainingCount, onClose, onConfirmed }: Props) => {
  const { data: receipt, isPending, isError } = useReceipt(receiptId)
  const updateReceipt = useUpdateReceipt(receiptId)
  const updateItem = useUpdateReceiptItem(receiptId)
  const confirmReceipt = useConfirmReceipt()
  const deleteReceipt = useDeleteReceipt()

  if (isPending) return <p className="p-4 text-sm text-muted-foreground">読み込み中…</p>
  if (isError || !receipt) return <p className="p-4 text-sm text-red-500">レシートを読み込めませんでした。</p>

  // 手で登録したレシートは PDF を持たない
  const isManual = receipt.source_path === null

  const handleReceiptChange = (params: UpdateReceiptParams) => {
    updateReceipt.mutate(params, { onError: (error) => notifyError(error, '保存に失敗しました。') })
  }

  const handleItemChange = (itemId: number, params: UpdateReceiptItemParams) => {
    updateItem.mutate({ itemId, params }, { onError: (error) => notifyError(error, '保存に失敗しました。') })
  }

  const handleConfirm = () => {
    confirmReceipt.mutate(receiptId, {
      onSuccess: () => {
        toast.success('確定しました。')
        onConfirmed()
      },
      onError: (error) => notifyError(error, '確定に失敗しました。'),
    })
  }

  const handleDelete = async () => {
    const ok = await confirmDialog({ title: 'このレシートを削除しますか？', destructive: true })
    if (!ok) return
    deleteReceipt.mutate(receiptId, {
      onSuccess: () => {
        toast.success('削除しました。')
        onClose()
      },
      onError: (error) => notifyError(error, '削除に失敗しました。'),
    })
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-gray-700 px-4 py-2">
        <Button variant="ghost" size="sm" onClick={onClose}>
          一覧へ
        </Button>
        <span className="truncate">{receipt.store_name || '店名なし'}</span>
        <span className="ml-auto shrink-0 tabular-nums">{formatAmount(receipt.total_amount)}</span>
        <span className="shrink-0 text-sm text-muted-foreground tabular-nums">残り {remainingCount} 件</span>
        {isManual && (
          <Button variant="outline" size="sm" onClick={handleDelete} disabled={deleteReceipt.isPending}>
            削除
          </Button>
        )}
        <Button size="sm" onClick={handleConfirm} disabled={confirmReceipt.isPending}>
          確定して次へ
        </Button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        {!isManual && (
          <div className="min-h-0 lg:w-1/2">
            <ReceiptPdfView receiptId={receipt.id} pageCount={receipt.page_count} />
          </div>
        )}
        <div className={`flex min-h-0 flex-col gap-4 overflow-y-auto p-4 ${isManual ? 'flex-1' : 'lg:w-1/2'}`}>
          {isManual && (
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <PencilLine className="size-4" />
              レシートなし（手で登録）
            </p>
          )}
          <ReceiptIssuePanel receipt={receipt} onAdopt={handleReceiptChange} />
          <ReceiptFieldGrid receipt={receipt} onChange={handleReceiptChange} />
          <ReceiptPlaceSection receipt={receipt} />
          <section className="flex flex-col gap-2">
            <h3 className="text-sm text-muted-foreground">明細</h3>
            <ReceiptItemTable
              items={receipt.items}
              editedFields={new Set(receipt.edited_fields)}
              onChange={handleItemChange}
            />
          </section>
        </div>
      </div>
    </div>
  )
}
